import React, { useRef } from "react";
import PropTypes from "prop-types";
import { List, ListItem, CircularProgress } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { usePaginationObserver } from "../../util/utilFunctions";
import ROUTINE_TYPES from "../../util/RoutineTypes";

import SupersetCircuitRecord from "./SupersetCircuitRecord";

const useStyles = makeStyles({
  listContainer: {
    overflowY: "auto",
    height: "88%",
    padding: 0,
  },

  ListHeadingText: {
    fontFamily: 'Archivo-Medium, sans-serif',
    fontWeight: 500,
    fontSize: 14,
  },
});

function LoadingSpinner() {
  return (
    <ListItem
      key="loading spinner"
      style={{ display: "flex", justifyContent: "center" }}
    >
      <CircularProgress size={20} />
    </ListItem>
  );
}

const SupersetRecordWithRef = React.forwardRef((props, ref) => {
  return <SupersetCircuitRecord lastSuperSetRecordRef={ref} {...props} />;
});

function SupersetCircuitList(props) {
  const classes = useStyles(props);
  const {
    superSets,
    selectedExerciseId,
    setSelectedExerciseId,
    currentTab,
    viewSuperSetsLink,
    setSuperSetData,
    setSupersetPageNumber,
    hasMoreSuperset,
    isSuperSetLoading,
    viewCircuitLink,
    setCircuitsData,
    setIsExerciseDataRefresh,
  } = props;
  const favoriteCircuits = superSets?.filter(
    (circuit) =>
      circuit.routine_type === ROUTINE_TYPES.CIRCUIT && circuit?.is_favorite,
  );

  const nonFavoriteCircuits = superSets?.filter(
    (circuit) =>
      circuit.routine_type === ROUTINE_TYPES.CIRCUIT && !circuit?.is_favorite,
  );

  const favoriteSuperSets = superSets?.filter(
    (superset) =>
      superset.routine_type === ROUTINE_TYPES.SUPER_SET && superset?.is_favorite,
  );

  const nonFavoriteSuperSets = superSets?.filter(
    (superset) =>
      superset.routine_type === ROUTINE_TYPES.SUPER_SET &&
      !superset?.is_favorite,
  );

  const rootElement = useRef();
  const lastSuperSetRecordRef = usePaginationObserver({
    setPageNumber: setSupersetPageNumber,
    isLoading: isSuperSetLoading,
    hasMoreDataToLoad: hasMoreSuperset,
    rootElement: rootElement.current,
  });

  return (
    <List ref={rootElement} className={classes.listContainer}>
      {currentTab === "circuits" && (
        <>
          {(favoriteCircuits?.length > 0 ||
            nonFavoriteCircuits?.length > 0) && (
            <>
              {/* favorite supersets */}
              {favoriteCircuits?.map((exercise, index) => (
                <SupersetRecordWithRef
                  ref={
                    favoriteCircuits.length === index + 1
                      ? lastSuperSetRecordRef
                      : null
                  }
                  key={exercise.id}
                  value={index}
                  isSelected={selectedExerciseId === exercise.id}
                  onChangeSelectedExerciseId={setSelectedExerciseId}
                  exercise={exercise}
                  viewCircuitLink={viewCircuitLink}
                  setCircuitsData={setCircuitsData}
                  setIsExerciseDataRefresh={setIsExerciseDataRefresh}
                />
              ))}

              {/* no-favorite circuits */}
              {nonFavoriteCircuits?.map((exercise, index) => (
                <SupersetRecordWithRef
                  ref={
                    nonFavoriteCircuits.length === index + 1
                      ? lastSuperSetRecordRef
                      : null
                  }
                  key={exercise.id}
                  value={index}
                  isSelected={selectedExerciseId === exercise.id}
                  onChangeSelectedExerciseId={setSelectedExerciseId}
                  exercise={exercise}
                  viewCircuitLink={viewCircuitLink}
                  setCircuitsData={setCircuitsData}
                  setIsExerciseDataRefresh={setIsExerciseDataRefresh}
                />
              ))}
            </>
          )}
        </>
      )}
      {currentTab === "supersets" && (
        <>
          {(favoriteSuperSets?.length > 0 ||
            nonFavoriteSuperSets?.length > 0) && (
            <>
              {/* favorite supersets */}
              {favoriteSuperSets?.map((exercise, index) => (
                <SupersetRecordWithRef
                  ref={
                    favoriteSuperSets.length === index + 1
                      ? lastSuperSetRecordRef
                      : null
                  }
                  key={exercise.id}
                  value={index}
                  isSelected={selectedExerciseId === exercise.id}
                  onChangeSelectedExerciseId={setSelectedExerciseId}
                  exercise={exercise}
                  viewSuperSetsLink={viewSuperSetsLink}
                  setSuperSetData={setSuperSetData}
                  setIsExerciseDataRefresh={setIsExerciseDataRefresh}
                />
              ))}

              {/* no-favorite supersets */}
              {nonFavoriteSuperSets?.map((exercise, index) => (
                <SupersetRecordWithRef
                  ref={
                    nonFavoriteSuperSets.length === index + 1
                      ? lastSuperSetRecordRef
                      : null
                  }
                  key={exercise.id}
                  value={index}
                  isSelected={selectedExerciseId === exercise.id}
                  onChangeSelectedExerciseId={setSelectedExerciseId}
                  exercise={exercise}
                  viewSuperSetsLink={viewSuperSetsLink}
                  setSuperSetData={setSuperSetData}
                  setIsExerciseDataRefresh={setIsExerciseDataRefresh}
                />
              ))}
            </>
          )}
        </>
      )}
      {isSuperSetLoading && <LoadingSpinner />}
    </List>
  );
}

SupersetCircuitList.propTypes = {
  exerciseData: PropTypes.array,
  superSets: PropTypes.array,
  isLoading: PropTypes.bool,
  selectedExerciseId: PropTypes.number,
  setSelectedExerciseId: PropTypes.func,
  viewExerciseDetailsLink: PropTypes.func,
  setIsExerciseDataRefresh: PropTypes.func,
};

export default SupersetCircuitList;
